import * as React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Button, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

// Import track configuration functions
import { getAllTracks } from '../session-viewer/getSegmentDefinition';

// GraphQL imports
import { API } from 'aws-amplify';
import { createEventList } from '../../graphql/mutations';

export default function CreateNewEvent({ onEventCreated }) {
  const [formData, setFormData] = React.useState({});
  const [startDate, setStartDate] = React.useState(null);
  const [startTime, setStartTime] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [endTime, setEndTime] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  
  // Track selection state
  const [selectedTrack, setSelectedTrack] = React.useState('');
  const [availableTracks, setAvailableTracks] = React.useState([]);

  console.log('Form data:', formData);

  // Load track data on component mount
  React.useEffect(() => {
    try {
      const tracks = getAllTracks();
      setAvailableTracks(tracks);
    } catch (error) {
      console.error('Error loading track data:', error);
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const combineDateTime = (date, time) => {
    if (!date || !time) return null;
    
    // Combine date and time into a single dayjs object
    const combined = dayjs(date)
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);
    
    return combined.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.eventName) {
      alert('Please fill in the Event Name');
      return;
    }

    if (!selectedTrack) {
      alert('Please select a track');
      return;
    }

    if (!startDate || !startTime) {
      alert('Please set the event start date and time');
      return;
    }

    setIsLoading(true);

    try {
      // Combine date and time for start and end
      const eventStartDate = combineDateTime(startDate, startTime);
      const eventEndDate = combineDateTime(endDate, endTime);

      // Prepare the input for the GraphQL mutation
      const eventInput = {
        eventName: formData.eventName,
        eventVenue: selectedTrack, // Use eventVenue field to store the track name
        eventStartDate: eventStartDate,
        eventEndDate: eventEndDate,
        eventSource: 'manual',
        // Note: we're not setting eventId or id - let DynamoDB auto-generate the id
      };

      console.log('Creating event with data:', eventInput);

      // Call the GraphQL mutation
      const result = await API.graphql({
        query: createEventList,
        variables: {
          input: eventInput
        }
      });

      console.log('Event created successfully:', result);
      
      // Reset form after successful creation
      setFormData({});
      setSelectedTrack('');
      setStartDate(null);
      setStartTime(null);
      setEndDate(null);
      setEndTime(null);
      
      alert('Event created successfully!');
      
      // Call the callback to notify parent component
      if (onEventCreated) {
        onEventCreated(result);
      }
      
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Create New Event
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              required
              id="eventName"
              name="eventName"
              label="Event Name"
              fullWidth
              variant="standard"
              value={formData.eventName || ''}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="standard" required>
              <InputLabel>Select Track</InputLabel>
              <Select
                value={selectedTrack}
                label="Select Track"
                onChange={(e) => setSelectedTrack(e.target.value)}
              >
                {availableTracks.map((track) => (
                  <MenuItem key={track.trackId} value={track.trackName}>
                    {track.trackName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Empty space for layout balance */}
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Event Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker 
                label="Event Start Time" 
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Event End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker 
                label="Event End Time" 
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
              />
            </Grid>
          </LocalizationProvider>

          <Grid item xs={12}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              size="large"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Event...' : 'Create Event'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
} 